/**
 * Dashboard Service
 * Handles dashboard data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockPendingActions, mockDashboardStats } from '../mock/mockDashboard';
import { delay } from '../mock/mockData';
import type { PendingAction, DashboardStats, DashboardKPI, PaginatedPendingActions } from '../../types/dashboard';
import { formatDateTime, parseDate } from '../../utils/dateUtils';

// Transform backend KpiItem to frontend DashboardKPI
const transformKpiItem = (item: any, title: string): DashboardKPI => ({
  title,
  value: item.value?.toLocaleString() || '0',
  change: item.change || '0%',
  trend: (item.trend?.toLowerCase() || 'neutral') as 'up' | 'down' | 'neutral',
});

// Transform backend PendingActionResult to frontend PendingAction
const transformPendingAction = (item: any): PendingAction => ({
  id: item.id,
  caseSummary: item.caseSummary || '',
  priority: (item.priority?.toLowerCase() || 'low') as 'high' | 'medium' | 'low',
  createdAt: parseDate(item.createdAt) || new Date(),
  createdAtText: item.createdAtText || formatDateTime(item.createdAt),
  updatedAt: parseDate(item.updatedAt) || new Date(),
  updatedAtText: item.updatedAtText || formatDateTime(item.updatedAt),
  actionType: (item.actionType?.toLowerCase() || 'review') as 'review' | 'approve' | 'investigate',
  type: item.type,
});



class DashboardService {
  /**
   * Get dashboard statistics (KPIs)
   */
  static async getStats(): Promise<DashboardKPI[]> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return mockDashboardStats;
    }

    const response = await apiService.get<{
      isSuccess: boolean;
      data: DashboardStats;
      message?: string;
    }>('/admin/dashboard/stats');

    if (!response.isSuccess || !response.data) {
      throw new Error(response.message || 'Failed to fetch dashboard stats');
    }

    const stats = response.data;
    return [
      transformKpiItem(stats.activeMissions, 'Active Missions'),
      transformKpiItem(stats.newMissionsToday, 'New Missions Today'),
      transformKpiItem(stats.usersOnline, 'Users Online'),
    ];
  }

  /**
   * Get pending actions
   */
  static async getPendingActions(page: number = 1, pageSize: number = 10): Promise<PaginatedPendingActions> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const data = [...mockPendingActions].slice(start, end);
      return {
        items: data,
        total: mockPendingActions.length,
      };
    }

    const response = await apiService.get<{
      isSuccess: boolean;
      data: {
        items: any[];
        total: number;
      };
      message?: string;
    }>('/admin/dashboard/pending-actions', {
      params: {
        page,
        pageSize,
      }
    });

    if (!response.isSuccess || !response.data) {
      throw new Error(response.message || 'Failed to fetch pending actions');
    }

    const data = response.data.items.map(transformPendingAction);
    const total = response.data.total;

    return {
      items: data,
      total,
    };
  }
}

export { DashboardService };
